import { localStorageMiddleware } from './utils/local-storage';
import { configureStore } from '@reduxjs/toolkit';
import { createWrapper } from 'next-redux-wrapper';

import addonsReducer from './addons/addons.slice';
import { authApi } from './auth/auth.api';
import authReducer from './auth/auth.slice';
import { billboardApi } from './billboard/billboard.api';
import billboardFiltersReducer from './billboard/filters.slice';
import billboardEventsReducer from './billboard/billboardEvents.slice';
import billboardReducer from './billboard/billboard.slice';
import eventBillboardReducer from './billboard/eventBillboard.slice';
import bookingReducer from './booking/booking.slice';
import checkoutReducer from './checkout/checkout.slice';
import configReducer from './config/config.slice';
import { eventApi } from './event/event.api';
import eventReducer from './event/event.slice';
import msiReducer from './msi/msi.slice';
import { msiApi } from './msi/msi.api';
import msciReducer from './msci/msci.slice';
import { msciApi } from './msci/msci.api';
import { paymentApi } from './payment/payment.api';
import paymentReducer from './payment/payment.slice';
import { promotionsApi } from './promotions/promotions.api';
import promotionsReducer from './promotions/promotions.slice';
import saturatedReducer from './saturated/saturated.slice';
import { seatsIOApi } from './seats-io/seats-io.api';
import seatsIOReducer from './seats-io/seats-io.slice';
import { summaryApi } from './summary/summary.api';
import summaryReducer from './summary/summary.slice';
import surveysReducer from './surveys/surveys.slice';
import { surveysApi } from './surveys/surveys.api';
import { ticketTypeApi } from './ticket-types/ticket-type.api';
import ticketTypesReducer from './ticket-types/ticket-type.slice';
import ticketsSelectedReducer from './tickets-selected/tickets-selected.slice';
import bayonetReducer from './bayonet/bayonet.slice';
import securityReducer from './security/security.slice';
import { securityApi } from './security/security.api';
import { bayonetApi } from './bayonet/bayonet.api';
import pathsReducer from './stepper/stepper.slice';
import customLinksReducer from './custom-links/customLinks.slice';
import timerReducer from './timer/timer.slice';
import { restoreBookingApi } from './restore-booking/restoreBooking.api';

import { cartelerasdApi } from './carteleras/billboard.api';

import rsvpReducer from './rsvp/rsvp.slice';
import { rsvpApi } from './rsvp/rsvp.api';

import { orderApi } from './api/order.api';
import { checkoutApi } from './checkout/checkout.api';
import { stripeApi } from './stripe/stripe.api';

export const makeStore = () =>
  configureStore({
    reducer: {
      addons: addonsReducer,
      auth: authReducer,
      booking: bookingReducer,
      bayonet: bayonetReducer,
      billboardFilters: billboardFiltersReducer,
      billboardEvents: billboardEventsReducer,
      billboard: billboardReducer,
      checkout: checkoutReducer,
      config: configReducer,
      event: eventReducer,
      msi: msiReducer,
      msci: msciReducer,
      payment: paymentReducer,
      promotions: promotionsReducer,
      saturated: saturatedReducer,
      seatsIO: seatsIOReducer,
      security: securityReducer,
      summary: summaryReducer,
      surveys: surveysReducer,
      ticketsSelected: ticketsSelectedReducer,
      ticketTypes: ticketTypesReducer,
      rsvp: rsvpReducer,
      eventBillboard: eventBillboardReducer,
      paths: pathsReducer,
      customLinks: customLinksReducer,
      timer: timerReducer,
      [authApi.reducerPath]: authApi.reducer,
      [billboardApi.reducerPath]: billboardApi.reducer,
      [checkoutApi.reducerPath]: checkoutApi.reducer,
      [stripeApi.reducerPath]: stripeApi.reducer,
      [eventApi.reducerPath]: eventApi.reducer,
      [msiApi.reducerPath]: msiApi.reducer,
      [msciApi.reducerPath]: msciApi.reducer,
      [orderApi.reducerPath]: orderApi.reducer,
      [paymentApi.reducerPath]: paymentApi.reducer,
      [promotionsApi.reducerPath]: promotionsApi.reducer,
      [seatsIOApi.reducerPath]: seatsIOApi.reducer,
      [securityApi.reducerPath]: securityApi.reducer,
      [summaryApi.reducerPath]: summaryApi.reducer,
      [surveysApi.reducerPath]: surveysApi.reducer,
      [ticketTypeApi.reducerPath]: ticketTypeApi.reducer,
      [rsvpApi.reducerPath]: rsvpApi.reducer,
      [bayonetApi.reducerPath]: bayonetApi.reducer,
      [cartelerasdApi.reducerPath]: cartelerasdApi.reducer,
      [restoreBookingApi.reducerPath]: restoreBookingApi.reducer,
    },
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware().concat(
        localStorageMiddleware([
          'addons',
          'auth',
          'booking',
          'bayonet',
          'checkout',
          'event',
          'msi',
          'msci',
          'payment',
          'promotions',
          'security',
          'summary',
          'surveys',
          'ticketsSelected',
          'ticketTypes',
          'rsvp',
          'paths',
          'customLinks',
          'billboard',
        ]),
        authApi.middleware,
        billboardApi.middleware,
        checkoutApi.middleware,
        stripeApi.middleware,
        eventApi.middleware,
        msiApi.middleware,
        msciApi.middleware,
        orderApi.middleware,
        paymentApi.middleware,
        promotionsApi.middleware,
        seatsIOApi.middleware,
        securityApi.middleware,
        summaryApi.middleware,
        surveysApi.middleware,
        ticketTypeApi.middleware,
        rsvpApi.middleware,
        restoreBookingApi.middleware,
        bayonetApi.middleware,
        cartelerasdApi.middleware,
      ),
  });

export const wrapper = createWrapper(makeStore, { debug: false });

export { default as useAuth } from './auth/useAuth';
export { default as useBillboard } from './billboard/useBillboard';
export { default as useBillboardFilters } from './billboard/useBillboardFilters';
export { default as useBooking } from './booking/useBooking';
export { default as useCheckout } from './checkout/useCheckout';
export { default as useConfig } from './config/useConfig';
export { default as useEvent } from './event/useEvent';
export { default as useMsi } from './msi/useMsi';
export { default as useMsci } from './msci/useMsci';
export { default as usePayment } from './payment/usePayment';
export { default as usePromotions } from './promotions/usePromotions';
export { default as useResetStore } from './useResetStore';
export { default as useSaturated } from './saturated/useSaturated';
export { default as useSeatsIO } from './seats-io/useSeatsIO';
export { default as useSecurity } from './security/useSecurity';
export { default as useSummary } from './summary/useSummary';
export { default as useSurveys } from './surveys/useSurveys';
export { default as useTicketsSelected } from './tickets-selected/useTicketsSelected';
export { default as useTicketTypes } from './ticket-types/useTicketTypes';
export { default as useBayonet } from './bayonet/useBayonet';
export { default as useRsvp } from './rsvp/useRsvp';
export { default as useAddOns } from './addons/useAddOns';
export { default as usePaths } from './stepper/usePaths';
export { default as useCustomLinksActions } from './custom-links/useCustomLinksActions';
export { default as useBillboardId } from './billboard/useBillboardId';
export { default as useCountdownTimer } from './timer/useCountdownTimer';
